/**
 * Updated by mariushirjanu on 13/09/2019.
 */
import { util } from '../util';
import { GaDeferredService, IGADeferred } from './ga-deferred.service';
import { HttpDataService, IHttpDataCache } from '../http/http-data.service';
import { Injectable, OnDestroy } from '@angular/core';
import { ILov, IObject } from '@type_definitions/datatypes';
import { Lang } from '@app/shared/language/language-data.service';
import { AuthService } from '@app/shared/services/auth/auth.service';

export const LOV_COUNTRY:string = 'country';
export const LOV_CURRENCY:string = 'currency';
export const LOV_LOCAL_CURRENCY:string = 'localcurrency';
export const LOV_DOCTYPE:string = 'doctype';
export const LOV_STATE:string = 'state';
export const LOV_ACTIVE_ACSL_USERS:string = 'acslusers';
export const LOV_ACSL_COMCO:string = 'acslcomco';
export const LOV_ACSL_CURRENCY:string = 'acslcurrencies';
export const LOV_TIER_STATUS:string = 'DOTIEST';
export const LOV_PAYMENT_TYPE:string = 'DOITPAT';
export const LOV_PAYMENT_FREQUENCY:string = 'DOFEEFR';
export const LOV_CURRENCY_SOURCE:string = 'currencysources';
export const LOV_PROPOSE_ALTERNATIVES:string = 'PROPOSE_ALTERNATIVES';
export const LOV_UPDATE_METHOD:string = 'ORDER_STATE_UPDATE_METHOD';
export const LOV_BANK_COSTS_CHARGE_BEARER:string = 'BANK_COST_CHARGE_BEARER';
export const LOV_OEM_ACSL_RANKING:string = 'DOACSLR';
export const LOV_OEM_PROCESSING_MODE:string = 'DOPLATO';
export const LOV_OEM_SHIPPING_FLOW:string = 'DOSHPCU';
export const LOV_OEM_ORDER_MODE:string = 'DOORDMO';
export const LOV_CATALOG_TYPES:string = 'catalogtypes';
export const LOV_PRICE_B2B_PO:string = 'DOB2BPR';
export const LOV_ORDER_PROCESS:string = 'DOCENLO';
export const LOV_PROCESS_NON_CATALOG_ORDERS:string = 'PROCESSNONCATALOGORDERS';
export const LOV_TRAINING_LEVEL:string = 'DOTRAININGLEVEL';
export const LOV_FAQ_SUBJECT:string = 'FAQ_SUBJECT';
export const LOV_FAQ_CATEGORY:string = 'FAQ_CATEGORY';
export const LOV_WEBSHOP_TYPE:string = 'DOPROTY';
export const LOV_PAYMENT_STATUS2:string = 'DOPAYS2';
export const LOV_INVOICE_CURRENCY:string = 'DOINVCU';
export const LOV_YES_NO:string = 'DOYESNO';

type ILovStorage = {
  [key in Lang]?:IObject<ILov[]>;
};

/**
 * The `LovService` class is responsible for managing and retrieving lists of values (LOVs),
 * which are typically used for dropdowns or reference data in the application.
 * It provides methods to fetch and cache LOVs, ensuring efficient retrieval and minimal
 * performance overhead. The service also handles language-specific data fetching.
 *
 * The `LovService` implements the `OnDestroy` lifecycle hook to handle cleanup when the service is destroyed.
 */
@Injectable()
export class LovService implements OnDestroy {
  private lovStorage:ILovStorage = {};
  private readonly cache:IHttpDataCache<any>;
  private readonly listsReadyDeferred:IGADeferred<void>;

  constructor(
    private authService:AuthService,
    deferredService:GaDeferredService,
    httpDataService:HttpDataService
  ) {
    this.cache = httpDataService.createCache();
    this.listsReadyDeferred = deferredService.defer();
  }

  /**
   * Fetches and stores all list values based on the specified language.
   *
   * @param {string} [lang=Lang.EN] - The language code for which the list values are to be fetched. Defaults to 'Lang.EN'.
   * @return {void} This method does not return anything but updates the local storage with fetched list values.
   */
  public loadAllLists(lang:string = Lang.EN):void {
    // if selected language has been loaded before, then do nothing
    if (util.notNullOrUndefinedOrEmpty(this.lovStorage[lang])) {
      return;
    }

    this.cache.doGET('rest/codevalues', { lang })
      .then((allCodeValues:IObject<ILov[]>) => {
        // update storage and mark as fetched
        this.lovStorage[lang] = allCodeValues;
      })
      .catch(util.noop)
      .finally(this.listsReadyDeferred.resolve);
  }

  /**
   * Retrieves a list of values based on the specified list of values (LOV) ID and language.
   *
   * @param {string} lovId - The identifier for the list of values to retrieve.
   * @param {string} [lang] - The language for the list of values. Defaults to the user interface language.
   * @return {Promise<any[]>} A promise that resolves to an array of values for the specified LOV ID and language.
   */
  public async list(lovId:string, lang:string = this.userLanguage):Promise<any[]> {
    return this.listsReadyDeferred.promise
      .then(() => this.getValues(lovId, lang));
  }

  /**
   * Retrieves a list of values based on the specified lookup ID and language.
   *
   * @param {string} lovId - The lookup ID used to identify the set of values to retrieve.
   * @param {string} lang - The language in which the values should be retrieved.
   * @return {Promise<any[]>} A promise that resolves to an array of values or an empty array if no values are found.
   */
  private getValues(lovId:string, lang:string):Promise<any[]> {
    if (util.notNullOrUndefinedOrEmpty(this.lovStorage[lang]?.[lovId])) {
      return Promise.resolve(this.lovStorage[lang]?.[lovId]);
    } else {
      const url:string = this.getUrl(lovId);

      if (!url) {
        return Promise.resolve([]);
      }

      return this.cache.doGET(
        url,
        { lang },
        undefined,
        `Error getting code values (${lovId})`
      );
    }
  }

  /**
   * Generates a URL based on the provided `lovId`. The method determines the appropriate endpoint
   * by applying conditions to match specific patterns of `lovId` or predefined constants.
   *
   * @param {string} lovId The identifier that specifies the type of URL to construct. Can be a constant
   *                       or a specific pattern such as 'localcurrency-{countryId}' or 'state-{countryId}'.
   * @return {string} The constructed URL string based on the given `lovId`.
   */
  private getUrl(lovId:string):string {
    let url:string;
    let countryId:string;

    if (lovId === LOV_COUNTRY) {
      url = 'rest/countries';
    } else if (lovId === LOV_CURRENCY) {
      url = 'rest/currencies';
    } else if (lovId === LOV_DOCTYPE) {
      url = 'rest/documenttypes';
    } else if (lovId === LOV_CATALOG_TYPES) {
      url = 'rest/bpprofiles/catalog/types';
    } else if (lovId === LOV_ACSL_COMCO) {
      url = 'rest/acslinfo/companycountries';
    } else if (lovId.match(/^localcurrency-(\w*)$/)) {
      countryId = lovId.match(/^localcurrency-(\w*)$/)[ 1 ];
      if (!countryId) {
        return;
      } else {
        url = `rest/codesets/localcurrency/${countryId}`;
      }
    } else if (lovId.match(/^state-(\w*)$/)) {
      countryId = lovId.match(/^state-(\w*)$/)[ 1 ];
      if (!countryId) {
        return;
      } else {
        url = `rest/states/${countryId}`;
      }
    } else {
      // this is a fail-safe to fetch single list code sets if somehow it doesn't exist in method 'getAllListValues'
      url = `rest/codesets/${lovId}`;
    }

    return url;
  }

  /**
   * Get user language or default to English
   *
   * @return {string} User language or default language to English
   */
  private get userLanguage():string {
    return this.authService.getCurrentUser()?.language?.isoCode || Lang.EN;
  }

  public ngOnDestroy():void {
    this.cache.clear();
    this.lovStorage = {};
  }
}